import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import {
  Plus, ChevronDown, ChevronUp, X, Save, Clock,
  AlertCircle, Gauge,
} from 'lucide-react';

const APPS_SCRIPT_URL = '/.netlify/functions/appsscript';   // logOdometer still uses this
// Valuations now run on Supabase (vehicle-valuation-sb).
const VEHICLE_VALUATION_URL = '/.netlify/functions/vehicle-valuation-sb';
const CURRENT_YEAR    = new Date().getFullYear();
const CARSALES_URL    = 'https://www.carsales.com.au/car-valuations/';
const REVAL_DAYS      = 90;   // re-value every 90 days

// ── Ownership helpers ─────────────────────────────────────────────────────────

function isPHB(owner) {
  return owner === 'PHB' || owner === 'PH Bouveng Pty Ltd';
}
function isHPL(owner) {
  return owner === 'HPL' || owner === 'Hollypaul Leasing Pty Ltd';
}

function OwnerBadge({ owner }) {
  if (isPHB(owner))
    return <ChipBadge bg="#2563eb" color="#fff" label="PHB" />;
  if (isHPL(owner))
    return <ChipBadge bg="var(--hp-gold)" color="#000" label="HPL" />;
  return null;
}

function ChipBadge({ bg, color, label }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 4,
      fontSize: '0.72rem', fontWeight: 700, fontFamily: 'var(--font-mono)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      background: bg, color,
    }}>
      {label}
    </span>
  );
}

// ── Recommendation logic ──────────────────────────────────────────────────────
//   Replace : 5+ yrs AND 150,000+ km  (both thresholds met)
//   Monitor : 4+ yrs OR  100,000+ km  (approaching either)
//   Keep    : under both thresholds

function getRecommendation(ageYears, latestKm) {
  if (ageYears === null && latestKm === null) return 'unknown';
  const isOld    = ageYears !== null && ageYears >= 5;
  const isHighKm = latestKm !== null && latestKm >= 150000;
  const isAgeing = ageYears !== null && ageYears >= 4;
  const isMedKm  = latestKm !== null && latestKm >= 100000;
  if (isOld && isHighKm) return 'replace';
  if (isOld || isHighKm || isAgeing || isMedKm) return 'monitor';
  return 'keep';
}

const REC_CONFIG = {
  replace: { bg: 'var(--hp-red)',     color: '#fff',              label: 'Replace', dot: 'var(--hp-red)'    },
  monitor: { bg: 'var(--hp-amber)',   color: '#000',              label: 'Monitor', dot: 'var(--hp-amber)'  },
  keep:    { bg: 'var(--hp-green)',   color: '#000',              label: 'Keep',    dot: 'var(--hp-green)'  },
  unknown: { bg: 'var(--hp-surface)', color: 'var(--hp-muted)',   label: 'No Data', dot: 'var(--hp-border)' },
};

function RecommendationBadge({ rec }) {
  const c = REC_CONFIG[rec] || REC_CONFIG.unknown;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 11px', borderRadius: 20,
      fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--font-mono)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      background: c.bg, color: c.color,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, opacity: 0.7 }} />
      {c.label}
    </span>
  );
}

// ── Format helpers ────────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Log Odometer Modal ────────────────────────────────────────────────────────

function OdometerLogModal({ vehicle, onClose, onSaved, addNotification }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate]         = useState(today);
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);

  const handleSave = async () => {
    const km = parseInt(odometer);
    if (!odometer || isNaN(km) || km <= 0) {
      addNotification('Please enter a valid odometer reading', 'error');
      return;
    }
    setSaving(true);
    try {
      const res  = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'logOdometer', rego: vehicle.rego, date, odometer: km, notes }),
      });
      const data = await res.json();
      if (data.success) {
        onSaved({ rego: vehicle.rego, date, odometer: km, notes });
        addNotification(`${vehicle.rego} — odometer logged: ${km.toLocaleString()} km`, 'success');
        onClose();
      } else {
        addNotification(`Save failed: ${data.error || 'unknown error'}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    fontSize: '0.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
    textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 5,
  };
  const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };
  const vehicleName = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ width: 420, background: 'var(--hp-bg)', borderRadius: 14, border: '1px solid var(--hp-border)', padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--hp-gold)', marginBottom: 5 }}>
              Asset Value · Odometer Log
            </div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: 700 }}>Log Odometer</h3>
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-muted)' }}>
              {vehicleName && <span>{vehicleName} · </span>}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)', fontWeight: 700 }}>{vehicle.rego}</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" className="input" style={inputStyle}
              value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Odometer (km)</label>
            <input type="number" className="input" style={inputStyle}
              value={odometer} onChange={e => setOdometer(e.target.value)}
              placeholder="e.g. 87450" min="0" step="1" />
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <input className="input" style={inputStyle}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Monthly check, service visit" />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save to Sheets'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Vehicle Card ──────────────────────────────────────────────────────────────

function fmt$(n) {
  if (!n) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

function VehicleCard({ vehicle, odometerLog, onLogOdometer, savedValuation, onSaveValuation }) {
  const [showHistory, setShowHistory] = useState(false);
  const [valEditing, setValEditing]   = useState(false);
  const [valSaving, setValSaving]     = useState(false);
  const [valError, setValError]       = useState('');
  const [valForm, setValForm]         = useState({
    retail:  savedValuation?.retail  != null ? String(savedValuation.retail)  : '',
    private: savedValuation?.private != null ? String(savedValuation.private) : '',
    tradeIn: savedValuation?.tradeIn != null ? String(savedValuation.tradeIn) : '',
  });

  // Re-sync the form when saved data loads/changes (and the user isn't mid-edit)
  useEffect(() => {
    if (valEditing) return;
    setValForm({
      retail:  savedValuation?.retail  != null ? String(savedValuation.retail)  : '',
      private: savedValuation?.private != null ? String(savedValuation.private) : '',
      tradeIn: savedValuation?.tradeIn != null ? String(savedValuation.tradeIn) : '',
    });
  }, [savedValuation, valEditing]);

  const vehicleLogs = useMemo(() => {
    return odometerLog
      .filter(l => (l.Rego || '').toUpperCase() === vehicle.rego.toUpperCase())
      .map(l => {
        const km = parseInt((l.Odometer || '').toString().replace(/,/g, ''));
        return { ...l, kmNum: isNaN(km) ? null : km };
      })
      .filter(l => l.kmNum !== null)
      .sort((a, b) => new Date(b.Date) - new Date(a.Date));
  }, [odometerLog, vehicle.rego]);

  const latestLog = vehicleLogs[0] || null;
  const latestKm  = latestLog ? latestLog.kmNum : null;

  // Annual km rate from oldest → latest reading
  const annualRate = useMemo(() => {
    if (vehicleLogs.length < 2) return null;
    const latest = vehicleLogs[0];
    const oldest = vehicleLogs[vehicleLogs.length - 1];
    const days = (new Date(latest.Date) - new Date(oldest.Date)) / 86400000;
    if (days < 30) return null;
    return Math.round((latest.kmNum - oldest.kmNum) / days * 365);
  }, [vehicleLogs]);

  const ageYears = vehicle.year ? CURRENT_YEAR - parseInt(vehicle.year) : null;
  const rec      = getRecommendation(ageYears, latestKm);

  const topBarColor = rec === 'replace' ? 'var(--hp-red)' : rec === 'monitor' ? 'var(--hp-amber)' : 'var(--hp-green)';
  const vehicleName = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');

  const ageColor = ageYears !== null && ageYears >= 5 ? 'var(--hp-amber)' : 'var(--hp-text)';
  const kmColor  = latestKm !== null
    ? (latestKm >= 150000 ? 'var(--hp-red)' : latestKm >= 100000 ? 'var(--hp-amber)' : 'var(--hp-text)')
    : 'var(--hp-text)';

  // ── Manual valuation (recorded from carsales) ──────────────────────────────
  const valuedAt    = savedValuation?.valuedAt || null;
  const valDays     = valuedAt ? Math.floor((Date.now() - new Date(valuedAt).getTime()) / 86400000) : null;
  const valDue      = valDays !== null && valDays >= REVAL_DAYS;
  const nextDueDate = valuedAt ? new Date(new Date(valuedAt).getTime() + REVAL_DAYS * 86400000) : null;

  // carsales valuation link, prefilled with the vehicle's keywords where possible
  const carsalesHref = (() => {
    const q = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    return q ? `${CARSALES_URL}?keyword=${encodeURIComponent(q)}` : CARSALES_URL;
  })();

  async function saveValuation() {
    const toNum = s => {
      const n = parseFloat(String(s).replace(/[,$\s]/g, ''));
      return isNaN(n) ? null : n;
    };
    const vals = { retail: toNum(valForm.retail), private: toNum(valForm.private), tradeIn: toNum(valForm.tradeIn) };
    if (vals.retail == null && vals.private == null && vals.tradeIn == null) {
      setValError('Enter at least one value first');
      return;
    }
    setValSaving(true); setValError('');
    try {
      const ok = await onSaveValuation(vehicle.rego, vals);
      if (ok) setValEditing(false);
      else setValError('Could not save — try again');
    } catch (e) {
      setValError('Could not save — try again');
    } finally {
      setValSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--hp-surface)',
      border: '1px solid var(--hp-border)',
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Top accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: topBarColor }} />

      <div style={{ padding: '22px 20px 0' }}>
        {/* Owner + Recommendation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <OwnerBadge owner={vehicle.owner} />
          <RecommendationBadge rec={rec} />
        </div>

        {/* Vehicle identity */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--hp-text)', marginBottom: 3 }}>
            {vehicleName || <span style={{ color: 'var(--hp-muted)', fontStyle: 'italic' }}>Details pending</span>}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.88rem', fontWeight: 800, color: 'var(--hp-gold)' }}>
            {vehicle.rego}
          </div>
        </div>

        {/* Age + Odometer stats */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div style={{ background: 'var(--hp-bg)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: '0.63rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
              Age
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: ageColor, lineHeight: 1 }}>
              {ageYears !== null ? `${ageYears} yr${ageYears !== 1 ? 's' : ''}` : '—'}
            </div>
            {vehicle.year && (
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', marginTop: 3 }}>since {vehicle.year}</div>
            )}
          </div>

          <div style={{ background: 'var(--hp-bg)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: '0.63rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
              Odometer
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: kmColor, lineHeight: 1 }}>
              {latestKm !== null
                ? <>{latestKm.toLocaleString()}<span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--hp-muted)', marginLeft: 2 }}>km</span></>
                : '—'
              }
            </div>
            {annualRate !== null && (
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', marginTop: 3 }}>
                ~{annualRate.toLocaleString()} km/yr
              </div>
            )}
          </div>
        </div>

        {/* Last logged */}
        {latestLog ? (
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 14 }}>
            <Clock size={11} />
            <span>Last logged: {fmtDate(latestLog.Date)}</span>
            {latestLog.Notes && (
              <span style={{ color: 'var(--hp-dim)' }}>· {latestLog.Notes}</span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 14 }}>
            <Gauge size={11} />
            No odometer readings yet
          </div>
        )}

        {/* Market value — manual entry from carsales */}
        <div style={{ background: 'var(--hp-bg)', borderRadius: 8, padding: '10px 12px', border: `1px solid ${valDue ? 'var(--hp-amber)' : 'var(--hp-border)'}`, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
            <div style={{ fontSize: '0.63rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Market Value
            </div>
            <a href={carsalesHref} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: '0.68rem', color: 'var(--hp-gold)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              How much is my car worth? ↗
            </a>
          </div>

          {valEditing ? (
            <>
              <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                {[['retail', 'Retail'], ['private', 'Private'], ['tradeIn', 'Trade-in']].map(([key, lbl]) => (
                  <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: '0.6rem', color: 'var(--hp-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>{lbl}</span>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', fontSize: '0.78rem', color: 'var(--hp-muted)' }}>$</span>
                      <input
                        type="text" inputMode="numeric" value={valForm[key]}
                        onChange={e => setValForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder="—"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '5px 6px 5px 16px', fontSize: '0.82rem', fontFamily: 'var(--font-mono)', fontWeight: 700, textAlign: 'right', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 5, color: 'var(--hp-text)' }}
                      />
                    </div>
                  </label>
                ))}
              </div>
              {valError && <div style={{ fontSize: '0.7rem', color: 'var(--hp-red)', marginBottom: 6 }}>{valError}</div>}
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} disabled={valSaving} onClick={saveValuation}>
                  <Save size={11} /> {valSaving ? 'Saving…' : 'Save Valuation'}
                </button>
                <button className="btn btn-ghost btn-sm" disabled={valSaving} onClick={() => { setValEditing(false); setValError(''); }}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {valuedAt ? (
                <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                  {[['Retail', savedValuation?.retail], ['Private', savedValuation?.private], ['Trade-in', savedValuation?.tradeIn]].map(([lbl, val]) => (
                    <div key={lbl} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.62rem', color: 'var(--hp-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{lbl}</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: val ? 'var(--hp-gold)' : 'var(--hp-muted)', fontFamily: 'var(--font-mono)' }}>{fmt$(val)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginBottom: 8 }}>
                  No valuation recorded yet. Check carsales, then enter the figures.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                {valuedAt ? (
                  <span style={{ fontSize: '0.68rem', color: valDue ? 'var(--hp-amber)' : 'var(--hp-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {valDue
                      ? <><AlertCircle size={10} /> Due for update ({valDays}d old)</>
                      : <>Valued {fmtDate(valuedAt)} · next due {fmtDate(nextDueDate)}</>}
                  </span>
                ) : <span />}
                <button onClick={() => { setValError(''); setValEditing(true); }} style={{ fontSize: '0.68rem', color: 'var(--hp-gold)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, whiteSpace: 'nowrap' }}>
                  {valuedAt ? 'Update ↗' : 'Enter values ↗'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, padding: '0 20px 20px' }}>
        <button
          className="btn btn-primary btn-sm"
          style={{ flex: 1 }}
          onClick={() => onLogOdometer(vehicle)}
        >
          <Plus size={12} /> Log Odometer
        </button>
        {vehicleLogs.length > 0 && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setShowHistory(h => !h)}
          >
            {showHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {vehicleLogs.length}
          </button>
        )}
      </div>

      {/* History drawer */}
      {showHistory && vehicleLogs.length > 0 && (
        <div style={{ borderTop: '1px solid var(--hp-border)', padding: '12px 20px 16px' }}>
          <div style={{ fontSize: '0.63rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Odometer History
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {vehicleLogs.slice(0, 10).map((log, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: i < Math.min(vehicleLogs.length, 10) - 1 ? '1px solid var(--hp-border)22' : 'none',
                }}
              >
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-muted)' }}>{fmtDate(log.Date)}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>
                    {log.kmNum.toLocaleString()}
                    <span style={{ fontWeight: 400, color: 'var(--hp-muted)', marginLeft: 2, fontSize: '0.7rem' }}>km</span>
                  </span>
                </div>
              </div>
            ))}
            {vehicleLogs.length > 10 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', textAlign: 'center', paddingTop: 6 }}>
                +{vehicleLogs.length - 10} older entries
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Asset Value Content (embeddable) ─────────────────────────────────────────
// Exported as a named export so it can be embedded inside FleetPage as a tab.
// Default export wraps it with standalone page chrome.

export function AssetValueContent() {
  const { vehicles, odometerLog, setOdometerLog, addNotification } = useApp();

  const [ownerFilter, setOwnerFilter] = useState('all');   // 'all' | 'PHB' | 'HPL'
  const [sortBy, setSortBy]           = useState('rec');   // 'rec' | 'age' | 'km' | 'rego'
  const [logModal, setLogModal]       = useState(null);    // vehicle object or null
  const [valuations, setValuations]   = useState({});      // { REGO: { retail, private, tradeIn, notes, valuedAt, valuedBy } }

  // Load saved manual valuations once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(VEHICLE_VALUATION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getVehicleValuations' }),
        });
        const data = await res.json();
        if (alive && data && data.success && Array.isArray(data.valuations)) {
          const map = {};
          data.valuations.forEach(v => { if (v.rego) map[String(v.rego).toUpperCase()] = v; });
          setValuations(map);
        }
      } catch (e) { /* non-fatal — manual entry still works */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleSaveValuation = useCallback(async (rego, vals) => {
    const key = String(rego).toUpperCase();
    try {
      const res = await fetch(VEHICLE_VALUATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveVehicleValuation', rego: key, ...vals }),
      });
      const data = await res.json();
      if (data && data.success) {
        const saved = data.valuation || { rego: key, ...vals, valuedAt: new Date().toISOString() };
        setValuations(prev => ({ ...prev, [key]: { ...prev[key], ...saved } }));
        if (addNotification) addNotification(`Valuation saved for ${key}`);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [addNotification]);

  // Only PHB + HPL vehicles
  const internalVehicles = useMemo(
    () => vehicles.filter(v => isPHB(v.owner) || isHPL(v.owner)),
    [vehicles]
  );

  const phbCount = useMemo(() => internalVehicles.filter(v => isPHB(v.owner)).length, [internalVehicles]);
  const hplCount = useMemo(() => internalVehicles.filter(v => isHPL(v.owner)).length, [internalVehicles]);

  // Enrich each vehicle with computed recommendation
  const enriched = useMemo(() =>
    internalVehicles.map(v => {
      const logs = odometerLog
        .filter(l => (l.Rego || '').toUpperCase() === v.rego.toUpperCase())
        .map(l => { const km = parseInt((l.Odometer || '').toString().replace(/,/g, '')); return isNaN(km) ? null : km; })
        .filter(km => km !== null);
      const latestKm = logs.length > 0 ? Math.max(...logs) : null;
      const ageYears = v.year ? CURRENT_YEAR - parseInt(v.year) : null;
      return { ...v, latestKm, ageYears, rec: getRecommendation(ageYears, latestKm) };
    }),
    [internalVehicles, odometerLog]
  );

  const replaceCount = enriched.filter(v => v.rec === 'replace').length;
  const monitorCount = enriched.filter(v => v.rec === 'monitor').length;

  // Fleet value from saved manual valuations (retail figure)
  const fleetValue = useMemo(() => {
    let sum = 0, n = 0;
    internalVehicles.forEach(v => {
      const val = valuations[v.rego.toUpperCase()];
      if (val && val.retail != null && val.retail !== '') { sum += Number(val.retail) || 0; n++; }
    });
    return { sum, n };
  }, [internalVehicles, valuations]);

  const displayed = useMemo(() => {
    let list = enriched;
    if (ownerFilter === 'PHB') list = list.filter(v => isPHB(v.owner));
    if (ownerFilter === 'HPL') list = list.filter(v => isHPL(v.owner));
    const recOrder = { replace: 0, monitor: 1, keep: 2, unknown: 3 };
    if (sortBy === 'rec')  return [...list].sort((a, b) => recOrder[a.rec] - recOrder[b.rec]);
    if (sortBy === 'age')  return [...list].sort((a, b) => (b.ageYears ?? -1) - (a.ageYears ?? -1));
    if (sortBy === 'km')   return [...list].sort((a, b) => (b.latestKm ?? -1) - (a.latestKm ?? -1));
    if (sortBy === 'rego') return [...list].sort((a, b) => a.rego.localeCompare(b.rego));
    return list;
  }, [enriched, ownerFilter, sortBy]);

  const handleOdometerSaved = useCallback(entry => {
    setOdometerLog(prev => [
      ...prev,
      {
        Rego:     entry.rego.toUpperCase(),
        Date:     entry.date,
        Odometer: String(entry.odometer),
        Notes:    entry.notes || '',
      },
    ]);
  }, [setOdometerLog]);

  return (
    <>

        {/* ── Summary stats ─────────────────────────────────────────────────── */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 24 }}>
          {[
            { label: 'PHB Vehicles',      value: phbCount,     accent: '#2563eb' },
            { label: 'HPL Vehicles',      value: hplCount,     accent: 'var(--hp-gold)' },
            { label: 'Needs Attention',
              value: replaceCount > 0 ? `${replaceCount} replace` : monitorCount > 0 ? `${monitorCount} monitor` : 'All clear',
              accent: replaceCount > 0 ? 'var(--hp-red)' : monitorCount > 0 ? 'var(--hp-amber)' : 'var(--hp-green)',
            },
            { label: 'Est. Fleet Value',
              value: fleetValue.n > 0 ? fmt$(fleetValue.sum) : '—',
              subtext: fleetValue.n > 0 ? `${fleetValue.n} of ${internalVehicles.length} valued` : 'Add valuations below',
              accent: fleetValue.n > 0 ? 'var(--hp-gold)' : 'var(--hp-border)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: s.label === 'Needs Attention' ? '1.3rem' : '2rem' }}>{s.value}</div>
              {s.subtext && <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 2 }}>{s.subtext}</div>}
            </div>
          ))}
        </div>

        {/* ── Replace alert banner ───────────────────────────────────────────── */}
        {replaceCount > 0 && (
          <div style={{
            background: 'var(--hp-surface)',
            border: '1px solid var(--hp-red)55',
            borderRadius: 10, marginBottom: 20, padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <AlertCircle size={16} color="var(--hp-red)" />
            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {replaceCount} vehicle{replaceCount !== 1 ? 's meet' : ' meets'} the replacement criteria
              <span style={{ fontWeight: 400, color: 'var(--hp-dim)', marginLeft: 6 }}>
                — 5+ years old and 150,000+ km
              </span>
            </span>
          </div>
        )}

        {/* ── Filter & sort bar ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['all','All'],['PHB','PHB'],['HPL','HPL']].map(([val, label]) => (
              <button
                key={val}
                className={`btn btn-sm ${ownerFilter === val ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setOwnerFilter(val)}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--hp-border)' }} />

          <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)' }}>Sort:</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['rec','Priority'],['age','Age'],['km','Odometer'],['rego','Rego']].map(([val, label]) => (
              <button
                key={val}
                className={`btn btn-sm ${sortBy === val ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSortBy(val)}
              >
                {label}
              </button>
            ))}
          </div>

          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--hp-muted)' }}>
            {displayed.length} vehicle{displayed.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Legend ────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 18, marginBottom: 20, fontSize: '0.74rem', color: 'var(--hp-muted)', flexWrap: 'wrap' }}>
          {[
            { dot: 'var(--hp-green)', label: 'Keep — under 4 years & under 100,000 km' },
            { dot: 'var(--hp-amber)', label: 'Monitor — 4+ years or 100,000+ km' },
            { dot: 'var(--hp-red)',   label: 'Replace — 5+ years AND 150,000+ km' },
          ].map(({ dot, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0, display: 'inline-block' }} />
              {label}
            </span>
          ))}
        </div>

        {/* ── Vehicle grid ──────────────────────────────────────────────────── */}
        {displayed.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--hp-muted)', fontSize: '0.9rem' }}>
            No PHB or HPL vehicles found in fleet data.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(310px, 100%), 1fr))', gap: 16 }}>
            {displayed.map(v => (
              <VehicleCard
                key={v.rego}
                vehicle={v}
                odometerLog={odometerLog}
                onLogOdometer={setLogModal}
                savedValuation={valuations[v.rego.toUpperCase()]}
                onSaveValuation={handleSaveValuation}
              />
            ))}
          </div>
        )}

      {/* ── Log odometer modal ─────────────────────────────────────────────── */}
      {logModal && (
        <OdometerLogModal
          vehicle={logModal}
          onClose={() => setLogModal(null)}
          onSaved={handleOdometerSaved}
          addNotification={addNotification}
        />
      )}
    </>
  );
}

// ── Standalone page (keeps the /asset-value route working if needed) ──────────

export default function AssetValuePage() {
  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Asset Value</h2>
          <p>PHB &amp; HPL vehicles — internal assessment &amp; replacement planning</p>
        </div>
      </div>
      <div className="page-body">
        <AssetValueContent />
      </div>
    </>
  );
}
